import React, { useState, useEffect } from 'react';
import { useGameStore, useGameActions } from '../hooks/useGameStore';

interface Props {
  onStart: () => void;
  onLoad: () => boolean;
}

export const TitleScreen: React.FC<Props> = ({ onStart, onLoad }) => {
  const state = useGameStore();
  const actions = useGameActions();
  const [flickerOpacity, setFlickerOpacity] = useState(1);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFlickerOpacity(0.3 + Math.random() * 0.7);
    }, 150);

    const timer = setTimeout(() => setShowSubtitle(true), 1500);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);

  const handleLoad = () => {
    const success = onLoad();
    if (!success) {
      alert('セーブデータがありません。');
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0a0a 50%, #0a0a1a 100%)',
      color: '#ddd',
      fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background noise effect */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
        pointerEvents: 'none',
      }} />

      <h1 style={{
        fontSize: 'clamp(2rem, 8vw, 4rem)',
        fontWeight: 300,
        letterSpacing: '0.3em',
        marginBottom: '0.5rem',
        opacity: flickerOpacity,
        transition: 'opacity 0.1s',
        textShadow: '0 0 20px rgba(200, 50, 50, 0.5)',
      }}>
        残響の校舎
      </h1>

      <p style={{
        fontSize: 'clamp(1rem, 3vw, 1.4rem)',
        letterSpacing: '0.5em',
        color: '#888',
        opacity: showSubtitle ? 1 : 0,
        transition: 'opacity 2s ease-in',
        marginBottom: '4rem',
      }}>
        ― Echo of the Lost ―
      </p>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        opacity: showSubtitle ? 1 : 0,
        transition: 'opacity 2s ease-in 0.5s',
      }}>
        <button
          onClick={onStart}
          style={{
            padding: '1rem 3rem',
            fontSize: '1.3rem',
            background: 'transparent',
            border: '1px solid #555',
            color: '#ccc',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.3em',
            transition: 'all 0.3s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#a33';
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.textShadow = '0 0 10px rgba(200,50,50,0.5)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#555';
            e.currentTarget.style.color = '#ccc';
            e.currentTarget.style.textShadow = 'none';
          }}
        >
          はじめから
        </button>

        <button
          onClick={handleLoad}
          style={{
            padding: '0.8rem 3rem',
            fontSize: '1.1rem',
            background: 'transparent',
            border: '1px solid #333',
            color: '#777',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.3em',
            transition: 'all 0.3s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#666';
            e.currentTarget.style.color = '#aaa';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#333';
            e.currentTarget.style.color = '#777';
          }}
        >
          つづきから
        </button>

        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            padding: '0.8rem 3rem',
            fontSize: '1.1rem',
            background: 'transparent',
            border: '1px solid #333',
            color: '#777',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.3em',
            transition: 'all 0.3s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#666';
            e.currentTarget.style.color = '#aaa';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#333';
            e.currentTarget.style.color = '#777';
          }}
        >
          設定
        </button>

        {showSettings && (
          <div style={{
            width: '280px',
            padding: '1rem 1.5rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid #333',
            borderRadius: '6px',
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#aaa',
                fontSize: '1rem',
                marginBottom: '0.4rem',
              }}>
                <span>BGM</span>
                <span>{Math.round(state.bgmVolume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(state.bgmVolume * 100)}
                onChange={e => actions.setBgmVolume(Number(e.target.value) / 100)}
                style={{ width: '100%', height: '4px', cursor: 'pointer', accentColor: '#c8a87c' }}
              />
            </div>
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#aaa',
                fontSize: '1rem',
                marginBottom: '0.4rem',
              }}>
                <span>効果音</span>
                <span>{Math.round(state.sfxVolume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(state.sfxVolume * 100)}
                onChange={e => actions.setSfxVolume(Number(e.target.value) / 100)}
                style={{ width: '100%', height: '4px', cursor: 'pointer', accentColor: '#c8a87c' }}
              />
            </div>
          </div>
        )}
      </div>

      <p style={{
        position: 'absolute',
        bottom: '2rem',
        fontSize: '0.9rem',
        color: '#444',
        letterSpacing: '0.2em',
      }}>
        ※ ヘッドフォン推奨
      </p>
    </div>
  );
};
